import { BaseClickable, Box, palette, Stack, style, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import { TFuncKey, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { LocationWithCareTypes } from '~/components/CareTeam/hooks/useCareTeam';
import { getImageObject } from '~/components/SolvPatternLibrary/Picture/util';
import { analyticsTrackEvent } from '~/core/analytics';
import { ACCOUNT_HOME_CLICK_BOOK_AGAIN_CDP } from '~/core/analytics/events';
import history from '~/core/history';
import { formatLocationName } from '~/core/util/location';
import dataTestIds from '~/test/test-ids';
import NextAvailable from '~/components/CareTeam/NextAvailable';

const LocationImage = styled.img.withConfig({ componentId: 'sc-1ouh59p-0' })(
  style({
    borderRadius: 'full',
    overflow: 'hidden',
    objectFit: 'fill',
    flexShrink: 0,
  })
);

type Props = {
  location: LocationWithCareTypes;
  className?: string;
};

/**
 * Card to be displayed on the account home that shows a given location,
 * the date the user last booked at that location, and timeslots for
 * rebooking today or in the future.
 */
const LocationBookAgainCardWithSlots = ({ location, className }: Props) => {
  const { t } = useTranslation(['account']);
  const servicesOfferedI18nKey = (): TFuncKey<['account']> => {
    if (location.careTypes.length === 1 && location.careTypes[0] === 'video') {
      return 'account:bookAgainCard.videoOnly';
    }
    if (location.careTypes.length === 2) {
      return 'account:bookAgainCard.both';
    }
    return 'account:bookAgainCard.ucOnly';
  };

  const image = getImageObject(location, { imageIndex: 0, showDefaultPictureIfMissing: true });

  const handleClick = () => {
    if (location.urlSlug) {
      history.push(location.urlSlug, { from: 'account' });
      analyticsTrackEvent(ACCOUNT_HOME_CLICK_BOOK_AGAIN_CDP, {
        location: location.id,
        lastBooked: location.lastBooked,
      });
    }
  };

  return (
    <Box
      className={className}
      style={{
        flexShrink: 0,
        maxWidth: '312px',
        width: '100%',
        alignSelf: 'stretch',
      }}
    >
      <Stack
        alignSelf="stretch"
        border="solid 1px"
        borderColor="gray-200"
        borderRadius="base"
        boxShadow="xs"
        data-testid={dataTestIds.account.bookAgainCard}
        overflow="hidden"
        width="100%"
      >
        <Stack flex="1" p={2} space={2}>
          <BaseClickable className={className} onClick={handleClick}>
            <Stack align-items="center" flex="1" justifyContent="space-between" row space={1}>
              <Stack row space={1}>
                <LocationImage height={44} src={image.fullRes} width={44} />
                <Stack alignSelf="stretch">
                  <Stack flex="1">
                    <Text variant="subtitle">{formatLocationName(location)}</Text>
                    {location.lastBooked ? (
                      <Text muted variant="body2">
                        {t('account:availability.lastBooked', {
                          date: location.lastBooked,
                        })}
                      </Text>
                    ) : (
                      <div style={{ marginBottom: '20px' }}></div>
                    )}
                  </Stack>
                </Stack>
              </Stack>
              <Box alignSelf="center">
                <ChevronRight htmlColor={palette.gray[500]} style={{ fontSize: 24 }} />
              </Box>
            </Stack>
          </BaseClickable>
          <NextAvailable location={location} />
        </Stack>
        <Stack bg="gray-100" px={2} py={1}>
          <Text>{t(servicesOfferedI18nKey())}</Text>
        </Stack>
      </Stack>
    </Box>
  );
};

export default LocationBookAgainCardWithSlots;
